/**
 * useConversationStream — the ONE conversation data layer, for both surfaces (the simplified landing and the detailed
 * `ConversationMessageList`). It alone owns the messages query, the live socket, the merge of the two and the poll:
 * a REST infinite query is the durable backfill, `useConversationEvents` supplies the live WebSocket turn, and the
 * two are merged by id. While the user's last message has no assistant answer yet it polls every 2 s so a dropped
 * socket still converges. One hook instance per conversation on a page — a second one is a second socket.
 */
import { useCallback, useEffect, useMemo } from 'react';
import {
  type InfiniteData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  listConversationMessagesInfiniteOptions,
  listConversationMessagesInfiniteQueryKey,
  listConversationMessagesQueryKey,
  replyToConversationMutation,
} from '../generated/api/@tanstack/react-query.gen.js';
import type { ConversationMessage, ConversationMessageList, MessageAttachment } from '../generated/api/types.gen.js';
import { buildReplyBody, type ConversationCommandContext } from './useConversationCommands.js';
import {
  useConversationEvents,
  type ConversationEventsState,
  type ConversationMessageView,
} from './useConversationEvents.js';
import type { ConversationMessagePart } from '@confluent/shared/lib/conversation-interactions';
import type { ConversationMemberRef, ConversationRecipient } from '@confluent/shared/lib/conversation-addressing';
import type { ConversationSurface } from '@confluent/shared/lib/conversation-surface';

export interface StreamMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  parts?: ConversationMessagePart[];
  createdAt: string;
  /** Per provider/model, matching `token_counts` and the generated API type (an ARRAY). */
  tokenCounts?: Array<{ provider: string; model: string; count: number }> | null;
  toolCalls?: Array<{ name: string; args?: unknown; result?: unknown; durationMs?: number }>;
  /** The member who wrote it (null: a system line or a tool row), and the members it is for. */
  author?: ConversationMemberRef | null;
  to?: ConversationRecipient[];
  /** The files a person attached (uploads; only their owner may download them). */
  attachments?: MessageAttachment[];
}

/**
 * A stored row as the thread reads it. The server stores a tool call as `{ id, name, input }`; the thread's tool-call
 * parts read `args`, so `input` is carried over as `args` here, at the one place stored rows enter the client.
 */
function toStreamMessage(message: ConversationMessage): StreamMessage {
  const { toolCalls, parts, ...rest } = message;
  return {
    ...rest,
    id: String(message.id),
    parts: parts as ConversationMessagePart[] | undefined,
    ...(toolCalls ? { toolCalls: toolCalls.map((call) => ({ name: call.name, args: call.input })) } : {}),
  };
}

export interface SendOptions {
  /** Slash-command selections of the detailed composer; the server reads them off the reply. */
  commandContext?: ConversationCommandContext | null;
  /** What the page shows right now (an entity assistant sends its form with every message). */
  contextSpec?: Record<string, unknown>;
  /** The members the message is for, by address; none: the conversation's lead (conversation-members spec). */
  to?: number[];
  /** The delivery addressed to the person that this message answers. */
  answers?: number;
  /** The person's uploaded files the message carries (upload ids); a message may be files alone. */
  attachments?: number[];
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const PAGE_QUERY = { limit: 25, sort_order: 'desc' } as const;

/** The request of the newest page; each further page adds the `after` cursor of the one before. */
function firstPage(id: string) {
  return { path: { id }, query: PAGE_QUERY };
}

/** Seed the backfill with a new conversation's messages, so its page opens on them instead of a blank thread. */
export function seedConversationMessages(
  queryClient: QueryClient,
  conversationId: string,
  messages: ConversationMessage[],
): void {
  const page: ConversationMessageList = {
    data: messages,
    paging: { cursors: { before: null, after: null }, hasMore: false, next: null },
    meta: { serverTime: new Date().toISOString() },
  };
  queryClient.setQueryData<InfiniteData<ConversationMessageList, ReturnType<typeof firstPage>>>(
    listConversationMessagesInfiniteQueryKey(firstPage(conversationId)),
    { pages: [page], pageParams: [firstPage(conversationId)] },
  );
}

/** Live messages win over backfill for the same id; result is asc by createdAt. */
export function mergeAllMessages(
  queryPages: StreamMessage[],
  liveMessages: ConversationMessageView[],
): StreamMessage[] {
  const liveMap = new Map(liveMessages.map((m) => [m.id, m]));
  // A live event carries a message's recipients as they stood when it was sent; the list is read again whenever a
  // receipt changes (`recipients.updated`), so the server's read of them wins.
  const readTo = new Map(queryPages.flatMap((m) => (m.to ? [[String(m.id), m.to] as const] : [])));
  const backfill = queryPages.map((m) => ({ ...m, id: String(m.id) })).filter((m) => !liveMap.has(m.id));
  const live: StreamMessage[] = liveMessages
    .filter((m) => m.role === 'user' || m.role === 'assistant' || m.role === 'system' || m.role === 'tool')
    .map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      parts: m.parts ?? [],
      ...(m.author !== undefined ? { author: m.author } : {}),
      ...(readTo.has(m.id) || m.to ? { to: readTo.get(m.id) ?? m.to } : {}),
      createdAt: m.createdAt,
    }));
  return [...backfill, ...live].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

/**
 * Is the user still waiting for an answer? Decided by the last CONVERSATIONAL message, ignoring tool/system rows. A
 * turn writes one tool message per tool call (21 of them on a multi-step request), so keying off the very last
 * message meant the first tool row flipped this false and killed the 2 s poll — the assistant answer that arrived
 * minutes later was then never fetched and the conversation looked dead. Tool rows say nothing about whether the
 * USER has been answered.
 */
export function isAwaitingAssistant(
  messages: readonly Pick<StreamMessage, 'role'>[],
  eventsState: Pick<
    ConversationEventsState,
    'sessionExpired' | 'streamingMessage' | 'agentThinking' | 'orchestratorTurn'
  >,
): boolean {
  let lastConversational: Pick<StreamMessage, 'role'> | null = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.role === 'user' || m.role === 'assistant') {
      lastConversational = m;
      break;
    }
  }
  return (
    lastConversational?.role === 'user' &&
    !eventsState.sessionExpired &&
    !eventsState.streamingMessage &&
    !eventsState.agentThinking &&
    eventsState.orchestratorTurn.status === 'idle'
  );
}

/** What a surface renders and acts on; pass it down rather than calling the hook a second time. */
export type ConversationStream = ReturnType<typeof useConversationStream>;

/**
 * `null` disables the stream entirely — no request, no socket — for a caller whose parent already owns it.
 * `surface` is where this stream is rendered; it goes out with every message, so the agent knows what the person can
 * see on that turn.
 */
export function useConversationStream(conversationId: string | null, surface: ConversationSurface) {
  const queryClient = useQueryClient();

  const {
    data,
    isLoading,
    isError,
    error,
    // Each further page is OLDER than the last — "next" in fetch order, earlier in the stream.
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...listConversationMessagesInfiniteOptions({
      path: { id: conversationId ?? '' },
      // NEWEST first. This was 'asc' with no forward paging, so the backfill fetched the
      // OLDEST 25 and could never reach past them: every conversation longer than a page
      // silently lost its most recent messages on reload, and they reappeared only if they
      // happened to arrive live over the socket in that session.
      query: PAGE_QUERY,
    }),
    enabled: Boolean(conversationId),
    initialPageParam: firstPage(conversationId ?? ''),
    // `hasMore` is the only field that means "there is more". `cursors.before` is just the first
    // row's cursor and is non-null whenever the page has ANY rows, so deriving from it offered
    // "Load earlier messages" on a two-message conversation, forever.
    getNextPageParam: (lastPage) =>
      lastPage.paging.hasMore && lastPage.paging.next
        ? { path: { id: conversationId ?? '' }, query: { ...PAGE_QUERY, after: lastPage.paging.next } }
        : undefined,
  });

  const eventsState = useConversationEvents({ conversationId, enabled: Boolean(conversationId) });

  const queryMessages = useMemo(() => data?.pages.flatMap((p) => p.data.map(toStreamMessage)) ?? [], [data]);
  const messages = useMemo(
    () => mergeAllMessages(queryMessages, eventsState.messages),
    [queryMessages, eventsState.messages],
  );

  const awaitingAssistant = isAwaitingAssistant(messages, eventsState);

  // A message's recipients changed (a member took it up, answered or failed it): read the messages again, so each
  // shows where it stands now rather than where it stood when it arrived (owner 2026-11-02).
  const { recipientsRevision } = eventsState;
  useEffect(() => {
    if (recipientsRevision === 0 || !conversationId) return;
    queryClient.invalidateQueries({ queryKey: listConversationMessagesQueryKey({ path: { id: conversationId } }) });
  }, [recipientsRevision, conversationId, queryClient]);

  useEffect(() => {
    if (!awaitingAssistant || !conversationId) return;
    const timer = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: listConversationMessagesQueryKey({ path: { id: conversationId } }) });
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [awaitingAssistant, conversationId, queryClient]);

  const replyMutation = useMutation({
    ...replyToConversationMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listConversationMessagesQueryKey({ path: { id: conversationId ?? '' } }),
      });
    },
  });

  const send = useCallback(
    (content: string, options: SendOptions = {}) => {
      if (!conversationId || (!content.trim() && !options.attachments?.length)) return;
      replyMutation.mutate(
        {
          path: { id: conversationId },
          body: buildReplyBody(content, options.commandContext ?? null, surface, options.contextSpec, {
            ...(options.to ? { to: options.to } : {}),
            ...(options.answers !== undefined ? { answers: options.answers } : {}),
            ...(options.attachments?.length ? { attachments: options.attachments } : {}),
          }),
        },
        { onSuccess: options.onSuccess, onError: options.onError },
      );
    },
    [conversationId, replyMutation, surface],
  );

  if (isError) console.error('useConversationStream: failed to load message backfill', error);

  return {
    messages,
    isLoading,
    isError,
    error,
    awaitingAssistant,
    eventsState,
    send,
    isSending: replyMutation.isPending,
    // Named for what they mean in the STREAM, not the fetch order: further pages are older.
    // `mergeAllMessages` sorts by createdAt, so pages arriving newest-first still display in order.
    fetchOlder: fetchNextPage,
    hasOlder: hasNextPage,
    isFetchingOlder: isFetchingNextPage,
  };
}
